import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { usePageTitle } from '../../hooks/usePageTitle';
import api from '../../api/client';
import { FechamentoCaixaItemDto, FechamentoCaixaResponse, FechamentoCaixaEncomendaResponse, ComissaoFuncionarioDto } from '../../types';

type AbaAtiva = 'pdv' | 'encomendas';

const FechamentoCaixa: React.FC = () => {
  usePageTitle('Fechamento de Caixa');
  const hoje = new Date().toISOString().split('T')[0];
  const [dataSelecionada, setDataSelecionada] = useState(hoje);
  const [dataConsulta, setDataConsulta] = useState<string | null>(null);
  const [abaAtiva, setAbaAtiva] = useState<AbaAtiva>('pdv');

  const { data: dadosPdv, isLoading: loadingPdv, isError: erroPdv } = useQuery({
    queryKey: ['fechamento-caixa', dataConsulta],
    queryFn: async () => {
      const response = await api.get<FechamentoCaixaResponse>('/fechamento-caixa', {
        params: { data: dataConsulta },
      });
      return response.data;
    },
    enabled: !!dataConsulta,
  });

  const { data: dadosEncomendas, isLoading: loadingEncomendas, isError: erroEncomendas } = useQuery({
    queryKey: ['fechamento-caixa-encomendas', dataConsulta],
    queryFn: async () => {
      const response = await api.get<FechamentoCaixaEncomendaResponse>('/fechamento-caixa/encomendas', {
        params: { data: dataConsulta },
      });
      return response.data;
    },
    enabled: !!dataConsulta,
  });

  const isLoading = loadingPdv || loadingEncomendas;

  const consolidado = useMemo(() => {
    const pdvVendas = dadosPdv?.valorTotalPedidos ?? 0;
    const pdvCusto = dadosPdv?.custoProdutosTotal ?? 0;
    const pdvFixo = dadosPdv?.custoFixo ?? 0;
    const pdvComissoes = dadosPdv?.totalComissoes ?? 0;
    const encVendas = dadosEncomendas?.valorTotalEncomendas ?? 0;
    const encCusto = dadosEncomendas?.custoProdutosTotal ?? 0;
    const encFixo = dadosEncomendas?.custoFixo ?? 0;

    const totalVendas = pdvVendas + encVendas;
    const totalCusto = pdvCusto + encCusto;
    const totalFixo = pdvFixo + encFixo;
    const totalLucro = totalVendas - totalCusto - totalFixo - pdvComissoes;

    return { totalVendas, totalCusto, totalFixo, pdvComissoes, totalLucro };
  }, [dadosPdv, dadosEncomendas]);

  const agruparItensCaixa = (itens: FechamentoCaixaItemDto[]) => {
    const mapa = new Map<number, FechamentoCaixaItemDto>();
    for (const item of itens) {
      if (mapa.has(item.produtoId)) {
        const g = mapa.get(item.produtoId)!;
        g.quantidade += item.quantidade;
        g.valorVenda += item.valorVenda;
        g.valorCusto += item.valorCusto;
      } else {
        mapa.set(item.produtoId, { ...item });
      }
    }
    return Array.from(mapa.values()).sort((a, b) => a.nomeProduto.localeCompare(b.nomeProduto, 'pt-BR'));
  };

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

  const formatTime = (isoDate: string) =>
    new Date(isoDate).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

  const handleCalcular = () => {
    setDataConsulta(dataSelecionada);
  };

  const temDados = dadosPdv || dadosEncomendas;
  const qtdPdv = dadosPdv?.pedidos.length ?? 0;
  const qtdEncomendas = dadosEncomendas?.encomendas.length ?? 0;

  return (
    <div className="animate-fadeIn">
      <h1 className="text-4xl font-display font-bold text-neutral-900 mb-6">Fechamento de Caixa</h1>

      {/* Seleção de data */}
      <div className="bg-white rounded-xl border border-neutral-100 shadow-md p-6 mb-6">
        <div className="flex items-end gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Data</label>
            <input
              type="date"
              value={dataSelecionada}
              max={hoje}
              onChange={(e) => setDataSelecionada(e.target.value)}
              className="px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <button
            onClick={handleCalcular}
            disabled={isLoading}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium"
          >
            {isLoading ? 'Calculando...' : 'Calcular'}
          </button>
        </div>
      </div>

      {dataConsulta && !isLoading && erroPdv && erroEncomendas && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 mb-6">
          Erro ao carregar o fechamento de caixa. Tente novamente.
        </div>
      )}

      {temDados && (
        <>
          {/* Resumo financeiro consolidado */}
          <div className="grid grid-cols-2 gap-4 mb-4 lg:grid-cols-4">
            <div className="bg-white rounded-xl border border-neutral-100 shadow-md p-5">
              <p className="text-sm text-neutral-500 mb-1">Total de Vendas</p>
              <p className="text-2xl font-bold text-primary-600">
                {formatCurrency(consolidado.totalVendas)}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                {qtdPdv} pedido(s) + {qtdEncomendas} encomenda(s)
              </p>
            </div>
            <div className="bg-white rounded-xl border border-neutral-100 shadow-md p-5">
              <p className="text-sm text-neutral-500 mb-1">Custo dos Produtos</p>
              <p className="text-2xl font-bold text-orange-500">
                {formatCurrency(consolidado.totalCusto)}
              </p>
            </div>
            <div className="bg-white rounded-xl border border-neutral-100 shadow-md p-5">
              <p className="text-sm text-neutral-500 mb-1">Custo Fixo (18%)</p>
              <p className="text-2xl font-bold text-yellow-600">
                {formatCurrency(consolidado.totalFixo)}
              </p>
            </div>
            <div className={`rounded-xl border border-neutral-100 shadow-md p-5 ${consolidado.totalLucro >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
              <p className="text-sm text-neutral-500 mb-1">Lucro Líquido</p>
              <p className={`text-2xl font-bold ${consolidado.totalLucro >= 0 ? 'text-success-600' : 'text-danger-600'}`}>
                {formatCurrency(consolidado.totalLucro)}
              </p>
            </div>
          </div>

          {/* Caixa de comissões */}
          {dadosPdv && dadosPdv.comissoes && dadosPdv.comissoes.length > 0 && (
            <div className="bg-white rounded-xl border border-neutral-100 shadow-md p-5 mb-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-neutral-700">Comissões de Funcionários (PDV)</h3>
                <span className="text-sm font-bold text-purple-700">
                  Total: {formatCurrency(dadosPdv.totalComissoes)}
                </span>
              </div>
              <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-neutral-500 border-b border-gray-100">
                    <th className="pb-1 font-medium">Funcionário</th>
                    <th className="pb-1 font-medium text-right">Total Vendas</th>
                    <th className="pb-1 font-medium text-right">Comissão</th>
                    <th className="pb-1 font-medium text-right">Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {dadosPdv.comissoes.map((c: ComissaoFuncionarioDto) => (
                    <tr key={c.usuarioId} className="border-b border-gray-50">
                      <td className="py-1 text-gray-700">{c.nomeFuncionario}</td>
                      <td className="py-1 text-right text-neutral-600">{formatCurrency(c.totalVendas)}</td>
                      <td className="py-1 text-right text-neutral-500">{c.percentual}%</td>
                      <td className="py-1 text-right font-semibold text-purple-700">{formatCurrency(c.valorComissao)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          )}

          {/* Abas */}
          <div className="bg-white rounded-xl border border-neutral-100 shadow-md overflow-hidden">
            <div className="flex border-b border-gray-200">
              <button
                onClick={() => setAbaAtiva('pdv')}
                className={`flex-1 py-3 px-6 text-sm font-semibold text-center transition-colors relative ${
                  abaAtiva === 'pdv'
                    ? 'text-primary-700 bg-primary-50'
                    : 'text-neutral-500 hover:text-neutral-700 hover:bg-neutral-50'
                }`}
              >
                Vendas (PDV)
                {qtdPdv > 0 && (
                  <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-medium ${
                    abaAtiva === 'pdv' ? 'bg-primary-200 text-primary-800' : 'bg-neutral-200 text-neutral-600'
                  }`}>
                    {qtdPdv}
                  </span>
                )}
                {abaAtiva === 'pdv' && (
                  <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-600" />
                )}
              </button>
              <button
                onClick={() => setAbaAtiva('encomendas')}
                className={`flex-1 py-3 px-6 text-sm font-semibold text-center transition-colors relative ${
                  abaAtiva === 'encomendas'
                    ? 'text-primary-700 bg-primary-50'
                    : 'text-neutral-500 hover:text-neutral-700 hover:bg-neutral-50'
                }`}
              >
                Encomendas
                {qtdEncomendas > 0 && (
                  <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-medium ${
                    abaAtiva === 'encomendas' ? 'bg-primary-200 text-primary-800' : 'bg-neutral-200 text-neutral-600'
                  }`}>
                    {qtdEncomendas}
                  </span>
                )}
                {abaAtiva === 'encomendas' && (
                  <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-600" />
                )}
              </button>
            </div>

            {/* Conteúdo da aba PDV */}
            {abaAtiva === 'pdv' && (
              <>
                {erroPdv ? (
                  <div className="p-6">
                    <p className="text-red-500 text-center py-4">
                      Erro ao carregar os pedidos do PDV.
                    </p>
                  </div>
                ) : dadosPdv && dadosPdv.pedidos.length === 0 ? (
                  <div className="p-6">
                    <p className="text-neutral-500 text-center py-4">
                      Nenhum pedido pago encontrado para esta data.
                    </p>
                  </div>
                ) : dadosPdv && (
                  <>
                    <div className="divide-y divide-gray-200">
                      {dadosPdv.pedidos.map((pedido) => (
                        <div key={pedido.id} className="p-6">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-3">
                              <span className="text-sm font-semibold text-gray-700">
                                Pedido #{pedido.id}
                              </span>
                              <span className="text-sm text-neutral-500">
                                {formatTime(pedido.dataHora)}
                              </span>
                              {pedido.numeroMesa && (
                                <span className="text-sm text-neutral-600">— Mesa {pedido.numeroMesa}</span>
                              )}
                              {pedido.nomeFuncionario && (
                                <span className="text-sm text-purple-600">— {pedido.nomeFuncionario}</span>
                              )}
                            </div>
                            <div className="flex items-center gap-6 text-sm">
                              <span className="text-neutral-500">
                                Custo: <span className="font-medium text-orange-500">{formatCurrency(pedido.custoTotal)}</span>
                              </span>
                              <span className="font-semibold text-gray-900">
                                Venda: {formatCurrency(pedido.valorTotal)}
                              </span>
                            </div>
                          </div>

                          <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="text-left text-xs text-neutral-500 border-b border-gray-100">
                                <th className="pb-1 font-medium">Produto</th>
                                <th className="pb-1 font-medium text-center">Qtd</th>
                                <th className="pb-1 font-medium text-right">Preço Unit.</th>
                                <th className="pb-1 font-medium text-right">Custo Unit.</th>
                                <th className="pb-1 font-medium text-right">Venda</th>
                                <th className="pb-1 font-medium text-right">Custo</th>
                              </tr>
                            </thead>
                            <tbody>
                              {agruparItensCaixa(pedido.itens).map((item) => (
                                <tr key={item.produtoId} className="border-b border-gray-50">
                                  <td className="py-1 text-gray-700">{item.nomeProduto}</td>
                                  <td className="py-1 text-center text-neutral-600">{item.quantidade}</td>
                                  <td className="py-1 text-right text-neutral-600">{formatCurrency(item.precoUnitario)}</td>
                                  <td className="py-1 text-right text-orange-500">{formatCurrency(item.custoUnitario)}</td>
                                  <td className="py-1 text-right font-medium text-neutral-900">{formatCurrency(item.valorVenda)}</td>
                                  <td className="py-1 text-right text-orange-500">{formatCurrency(item.valorCusto)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Rodapé PDV */}
                    <div className="px-6 py-4 bg-neutral-50 border-t border-gray-200">
                      <div className="flex justify-end gap-8 text-sm font-medium">
                        <span className="text-neutral-600">
                          Total de vendas: <span className="text-primary-600">{formatCurrency(dadosPdv.valorTotalPedidos)}</span>
                        </span>
                        <span className="text-neutral-600">
                          Custo produtos: <span className="text-orange-500">({formatCurrency(dadosPdv.custoProdutosTotal)})</span>
                        </span>
                        <span className="text-neutral-600">
                          Custo fixo: <span className="text-yellow-600">({formatCurrency(dadosPdv.custoFixo)})</span>
                        </span>
                        <span className={`font-bold ${dadosPdv.lucroLiquido >= 0 ? 'text-success-600' : 'text-danger-600'}`}>
                          Lucro líquido: {formatCurrency(dadosPdv.lucroLiquido)}
                        </span>
                      </div>
                    </div>
                  </>
                )}
              </>
            )}

            {/* Conteúdo da aba Encomendas */}
            {abaAtiva === 'encomendas' && (
              <>
                {erroEncomendas ? (
                  <div className="p-6">
                    <p className="text-red-500 text-center py-4">
                      Erro ao carregar as encomendas.
                    </p>
                  </div>
                ) : dadosEncomendas && dadosEncomendas.encomendas.length === 0 ? (
                  <div className="p-6">
                    <p className="text-neutral-500 text-center py-4">
                      Nenhuma encomenda entregue encontrada para esta data.
                    </p>
                  </div>
                ) : dadosEncomendas && (
                  <>
                    <div className="divide-y divide-gray-200">
                      {dadosEncomendas.encomendas.map((encomenda) => (
                        <div key={encomenda.id} className="p-6">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-3">
                              <span className="text-sm font-semibold text-gray-700">
                                Encomenda #{encomenda.id}
                              </span>
                              <span className="text-sm text-neutral-500">
                                {formatTime(encomenda.dataHoraEntrega)}
                              </span>
                              <span className="text-sm text-neutral-600">— {encomenda.nomeCliente}</span>
                            </div>
                            <div className="flex items-center gap-6 text-sm">
                              {encomenda.desconto > 0 && (
                                <span className="text-neutral-500">
                                  Desconto: <span className="font-medium text-purple-500">{formatCurrency(encomenda.desconto)}</span>
                                </span>
                              )}
                              <span className="text-neutral-500">
                                Custo: <span className="font-medium text-orange-500">{formatCurrency(encomenda.custoTotal)}</span>
                              </span>
                              <span className="font-semibold text-gray-900">
                                Venda: {formatCurrency(encomenda.valorTotal)}
                              </span>
                            </div>
                          </div>

                          <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="text-left text-xs text-neutral-500 border-b border-gray-100">
                                <th className="pb-1 font-medium">Produto</th>
                                <th className="pb-1 font-medium text-center">Qtd</th>
                                <th className="pb-1 font-medium text-right">Preço Unit.</th>
                                <th className="pb-1 font-medium text-right">Custo Unit.</th>
                                <th className="pb-1 font-medium text-right">Venda</th>
                                <th className="pb-1 font-medium text-right">Custo</th>
                              </tr>
                            </thead>
                            <tbody>
                              {agruparItensCaixa(encomenda.itens).map((item) => (
                                <tr key={item.produtoId} className="border-b border-gray-50">
                                  <td className="py-1 text-gray-700">{item.nomeProduto}</td>
                                  <td className="py-1 text-center text-neutral-600">{item.quantidade}</td>
                                  <td className="py-1 text-right text-neutral-600">{formatCurrency(item.precoUnitario)}</td>
                                  <td className="py-1 text-right text-orange-500">{formatCurrency(item.custoUnitario)}</td>
                                  <td className="py-1 text-right font-medium text-neutral-900">{formatCurrency(item.valorVenda)}</td>
                                  <td className="py-1 text-right text-orange-500">{formatCurrency(item.valorCusto)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Rodapé Encomendas */}
                    <div className="px-6 py-4 bg-neutral-50 border-t border-gray-200">
                      <div className="flex justify-end gap-8 text-sm font-medium">
                        <span className="text-neutral-600">
                          Total encomendas: <span className="text-primary-600">{formatCurrency(dadosEncomendas.valorTotalEncomendas)}</span>
                        </span>
                        <span className="text-neutral-600">
                          Custo produtos: <span className="text-orange-500">({formatCurrency(dadosEncomendas.custoProdutosTotal)})</span>
                        </span>
                        <span className="text-neutral-600">
                          Custo fixo: <span className="text-yellow-600">({formatCurrency(dadosEncomendas.custoFixo)})</span>
                        </span>
                        <span className={`font-bold ${dadosEncomendas.lucroLiquido >= 0 ? 'text-success-600' : 'text-danger-600'}`}>
                          Lucro líquido: {formatCurrency(dadosEncomendas.lucroLiquido)}
                        </span>
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default FechamentoCaixa;
